import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import {
  faCalendar,
  faCircle,
  faCircleInfo,
  faFilter,
  faSearch,
  faSort,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ProgramRow from './ProgramRow';
import { setYear } from 'state/slices/settingsSlice';
import {
  checksheetDelete,
  checksheetUpdateProgress,
} from 'apis/checksheetAPIs';
import useServiceAuth from 'auth/useServiceAuth';
import StaticData from './StaticData';
import { Button } from '@asu/components-core';
import { Dropdown, Modal } from 'react-bootstrap';
import DropdownCustomToggle from './DropdownCustomToggle';
import LoadingSkeleton from './LoadingSkeleton';
import { Link } from 'react-router-dom';
import { classNames } from 'utils';

const statusCircle = {
  DEPARTMENT: {
    color: 'text-gray-4',
  },
  COLLEGE: {
    color: 'text-gray-7',
  },
  PROVOST: {
    color: 'text-warning',
  },
  DARS: {
    color: 'text-info',
  },
  COMPLETE: {
    color: 'text-success',
  },
};

const ProgramRows = ({
  degreeType,
  collegeCode = null,
  collegeName = null,
  programList,
  // idsList,
  fetchPrograms,
  fetchSubplanActivationList,
  checksheetList,
  checksheetMap,
  refetchChecksheets,
  getChecksheetIsLoading,
  getChecksheetIsSuccess,
  setError,
  handleOpenDefinitions,
  user,
  year,
}) => {
  const dispatch = useDispatch();
  const { getAccessToken } = useServiceAuth();

  const userIsProvost = user?.role?.includes('PROVOST');

  const [searchString, setSearchString] = useState('');
  const [departments, setDepartments] = useState([]);
  const [locationFilters, setLocationFilters] = useState([]);
  const [departmentFilters, setDepartmentFilters] = useState([]);
  const [statusFilters, setStatusFilters] = useState([]);
  const [locationLabel, setLocationLabel] = useState('Location');
  const [departmentLabel, setDepartmentLabel] = useState('Department');
  const [statusLabel, setStatusLabel] = useState('Status');
  const [sortBy, setSortBy] = useState(() => sortByNameAToZ);
  const [selectedChecksheets, setSelectedChecksheets] = useState([]);
  const [allChecksheetsSelected, setAllChecksheetsSelected] = useState(false);
  const [programChecksheetList, setProgramChecksheetList] = useState([]);
  const [showDiv, setShowDiv] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const {
    mutate: updateChecksheetProgress,
  } = useMutation({
    mutationFn: checksheetUpdateProgress,
    onSuccess: async (data, { token }) => {
      refetchChecksheets({ year, token });
      console.log('updateChecksheetProgress success');
    },
  });

  const { mutate: deleteChecksheets } = useMutation({
    mutationFn: async ({ token }) => {
      const total = selectedChecksheets.length;
      const results = [];

      for (let i = 0; i < total; i++) {
        const result = await checksheetDelete({
          id: selectedChecksheets[i],
          token,
        });
        results.push(result);
      }
      return results;
    },
    onSuccess: () => {
      setSelectedChecksheets([]);
      refetchChecksheets();
      setShowModal(() => false);
    },
    onError: () => {
      refetchChecksheets();
      setShowModal(() => false);
    },
  });

  const handleClearChecksheets = async () => {
    const confirmDelete = window.confirm(
      `Are you sure you want to clear the checksheet for ${selectedChecksheets.length} programs?`
    );

    if (confirmDelete) {
      const token = await getAccessToken();

      setShowModal(() => true);
      await deleteChecksheets({ token });
    }
  };

  const handleSetProgress = async (progress) => {
    const jsonData = {
      ids: [...selectedChecksheets],
      progress,
      action: 'changed',
      resetRecalledBy: true,
    };
    const token = await getAccessToken();

    await updateChecksheetProgress({
      jsonData: jsonData,
      token: token,
    });
  };

  function sortByNameAToZ(a, b) {
    if (a.acadPlanDescription < b.acadPlanDescription) return -1;
    if (a.acadPlanDescription > b.acadPlanDescription) return 1;
    if (a.degreeDescriptionShort < b.degreeDescriptionShort) return -1;
    if (a.degreeDescriptionShort > b.degreeDescriptionShort) return 1;
    if (!!a.subplan && !!b.subplan) {
      if (a.subplan.description < b.subplan.description) return -1;
      if (a.subplan.description > b.subplan.description) return 1;
    }
    return 0;
  }

  function sortByNameZToA(a, b) {
    if (a.acadPlanDescription < b.acadPlanDescription) return 1;
    if (a.acadPlanDescription > b.acadPlanDescription) return -1;
    if (a.degreeDescriptionShort < b.degreeDescriptionShort) return 1;
    if (a.degreeDescriptionShort > b.degreeDescriptionShort) return -1;
    if (!!a.subplan && !!b.subplan) {
      if (a.subplan.description < b.subplan.description) return 1;
      if (a.subplan.description > b.subplan.description) return -1;
    }
    return 0;
  }

  const handleSortBy = (e) => {
    const value = e.target.value;
    setSortBy(
      value === 'nameZ-A' ? () => sortByNameZToA : () => sortByNameAToZ
    );
  };

  const handleSearch = (e) => {
    setSearchString(e.target.value);
  };

  const filterByLocation = useCallback(
    (program) => {
      if (locationFilters.length === 0) return true;
      let included = false;
      if (program.campusesOffered) {
        program.campusesOffered.forEach((campus) => {
          if (locationFilters.includes(campus.campusCode)) included = true;
        });
      }
      return included;
    },
    [locationFilters]
  );

  const handleFilterLocation = (e) => {
    const value = e.target.value;
    const currentLocations = [...locationFilters];
    if (locationFilters.includes(value)) {
      currentLocations.splice(locationFilters.indexOf(value), 1);
    } else {
      currentLocations.push(value);
    }
    if (currentLocations.length === 0) setLocationLabel('Location');
    else if (currentLocations.length === 1) {
      const label = StaticData.campusUndergradList.find(
        (campus) => campus.value === currentLocations[0]
      ).label;
      setLocationLabel(label);
    } else if (currentLocations.length > 1)
      setLocationLabel(`Location (${currentLocations.length})`);
    setLocationFilters(currentLocations);
  };

  const handleUncheckAllLocations = () => {
    setLocationLabel('Location');
    setLocationFilters([]);
  };

  const filterByCollege = useCallback(
    (program) => {
      let included = false;
      let mainOwner = {};
      if (program.owners.length > 0) {
        let highestPercent = 0;
        program.owners.forEach((programOwner) => {
          if (programOwner.percentOwned > highestPercent) {
            highestPercent = programOwner.percentOwned;
            mainOwner = programOwner;
          }
        });
      }
      if (!collegeCode || collegeCode === mainOwner.collegeAcadOrg)
        included = true;
      return included;
    },
    [collegeCode]
  );

  const filterByDepartment = useCallback(
    (program) => {
      let included = false;
      if (departmentFilters.length === 0) return true;
      if (program.owners) {
        program.owners.forEach((college) => {
          if (departmentFilters.includes(college.departmentAcadOrg))
            included = true;
        });
      }
      return included;
    },
    [departmentFilters]
  );

  const handleFilterDepartment = (e) => {
    const value = e.target.value;
    const currentDepartments = [...departmentFilters];
    if (departmentFilters.includes(value)) {
      currentDepartments.splice(departmentFilters.indexOf(value), 1);
    } else {
      currentDepartments.push(value);
    }
    if (currentDepartments.length === 0) setDepartmentLabel('Department');
    else if (currentDepartments.length === 1) {
      const label = departments.find(
        (department) => department[0] === currentDepartments[0]
      )[1];
      setDepartmentLabel(label);
    } else if (currentDepartments.length > 1)
      setDepartmentLabel(`Department (${currentDepartments.length})`);
    setDepartmentFilters(currentDepartments);
  };

  const handleUncheckAllDepartments = () => {
    setDepartmentLabel('Department');
    setDepartmentFilters([]);
  };

  const sortByDepartment = (a, b) => {
    if (a[1] > b[1]) return 1;
    if (a[1] < b[1]) return -1;
    return 0;
  };

  const filterByStatus = useCallback(
    (program) => {
      if (statusFilters.length === 0) return true;
      if (!checksheetMap) return true;

      const key = `${program.acadPlanCode}*${program.subplan?.acadSubPlanCode ?? 'null'}`;
      const checksheetFound = checksheetMap.get(key);

      let included = false;

      if (
        (checksheetFound?.status &&
          statusFilters.includes(checksheetFound.status)) ||
        (!checksheetFound && statusFilters.includes('NONE'))
      )
        included = true;

      if (
        fetchSubplanActivationList?.data?.[program.acadPlanCode] &&
        !program.subplan?.acadSubPlanCode
      ) {
        Object.entries(
          fetchSubplanActivationList.data[program.acadPlanCode]
        ).forEach(([subplanCode]) => {
          const subplanKey = `${program.acadPlanCode}*${subplanCode}`;
          const subplanChecksheetFound = checksheetMap.get(subplanKey);

          if (
            (subplanChecksheetFound?.status &&
              statusFilters.includes(subplanChecksheetFound.status)) ||
            (!subplanChecksheetFound && statusFilters.includes('NONE'))
          )
            included = true;
        });
      }

      return included;
    },
    [checksheetMap, fetchSubplanActivationList, statusFilters]
  );

  const filterByStatusForTotal = useCallback(
    (program) => {
      if (statusFilters.length === 0) return true;
      if (!checksheetMap) return true;

      const key = `${program.acadPlanCode}*${program.subplan?.acadSubPlanCode ?? 'null'}`;
      const checksheetFound = checksheetMap.get(key);

      let included = false;

      if (
        (checksheetFound?.status &&
          statusFilters.includes(checksheetFound.status)) ||
        (!checksheetFound && statusFilters.includes('NONE'))
      )
        included = true;

      return included;
    },
    [checksheetMap, statusFilters]
  );

  const handleFilterStatus = (e) => {
    const value = e.target.value;
    const currentStatuses = [...statusFilters];
    if (statusFilters.includes(value)) {
      currentStatuses.splice(statusFilters.indexOf(value), 1);
    } else {
      currentStatuses.push(value);
    }
    if (currentStatuses.length === 0) setStatusLabel('Status');
    else if (currentStatuses.length === 1) {
      let label = null;
      if (currentStatuses[0] !== 'NONE') {
        label = StaticData.statusArray[currentStatuses[0]];
      } else label = 'No checksheet exists';
      setStatusLabel(label);
    } else if (currentStatuses.length > 1)
      setStatusLabel(`Status (${currentStatuses.length})`);
    setStatusFilters(currentStatuses);
  };

  const handleUncheckAllStatuses = () => {
    setStatusLabel('Status');
    setStatusFilters([]);
  };

  const handleClearAllFilters = () => {
    setLocationLabel('Location');
    setLocationFilters([]);
    setDepartmentLabel('Department');
    setDepartmentFilters([]);
    setStatusLabel('Status');
    setStatusFilters([]);
  };

  const handleSetYear = (e) => {
    dispatch(setYear(e.target.value));
  };

  let frameId = useRef(null);

  const handleScroll = useCallback(() => {
    frameId.current = requestAnimationFrame(() => {
      const scrollPosition = window.scrollY;
      setShowDiv(scrollPosition > 185);
    });
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (frameId.current) cancelAnimationFrame(frameId.current);
    };
  }, [handleScroll]);

  useEffect(() => {
    const currentDepartments = [];
    programList.forEach((program) => {
      program.owners.forEach((owner) => {
        if (
          !currentDepartments.some(
            ([key, value]) => key === owner.departmentAcadOrg
          ) &&
          (!collegeCode || collegeCode === owner.collegeAcadOrg)
        ) {
          currentDepartments.push([
            owner.departmentAcadOrg,
            owner.departmentDescription,
          ]);
        }
      });
    });
    setDepartments(currentDepartments);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programList, collegeCode]);

  useEffect(() => {
    let filteredChecksheetList = [];

    if (degreeType === 'undergrad') {
      filteredChecksheetList = checksheetList.filter(
        (checksheet) =>
          checksheet.programType !== 'MIN' && checksheet.programType !== 'CERT'
      );
    } else if (degreeType === 'minor') {
      filteredChecksheetList = checksheetList.filter(
        (checksheet) => checksheet.programType === 'MIN'
      );
    } else if (degreeType === 'cert') {
      filteredChecksheetList = checksheetList.filter(
        (checksheet) => checksheet.programType === 'CERT'
      );
    }

    setProgramChecksheetList(filteredChecksheetList);
  }, [checksheetList, degreeType]);

  const { filteredPrograms, totalProgramsShown } = useMemo(() => {
    const filtered = programList
      .filter(filterByLocation)
      .filter(filterByCollege)
      .filter(filterByDepartment)
      .filter(
        (program) =>
          `${program.acadPlanDescription}${degreeType === 'undergrad' ? ', ' + program.degreeDescriptionShort : ''}`
            .toLowerCase()
            .includes(searchString.toLowerCase()) ||
          program.subplan?.description
            .toLowerCase()
            .includes(searchString.toLowerCase()) ||
          program.acadPlanCode
            .toLowerCase()
            .includes(searchString.toLowerCase()) ||
          program.subplan?.acadSubPlanCode
            .toLowerCase()
            .includes(searchString.toLowerCase())
      )
      .toSorted(sortBy);

    return {
      filteredPrograms: filtered.filter(filterByStatus),
      totalProgramsShown: filtered.filter(filterByStatusForTotal).length,
    };
  }, [
    degreeType,
    filterByCollege,
    filterByDepartment,
    filterByLocation,
    filterByStatus,
    filterByStatusForTotal,
    programList,
    searchString,
    sortBy,
  ]);

  useEffect(() => {
    const visibleChecksheetCount = filteredPrograms.filter((program) => {
      const key = `${program.acadPlanCode}*${program.subplan?.acadSubPlanCode ?? 'null'}`;
      return checksheetMap.has(key);
    }).length;

    if (
      selectedChecksheets.length === visibleChecksheetCount &&
      visibleChecksheetCount !== 0
    )
      setAllChecksheetsSelected(true);
    else setAllChecksheetsSelected(false);
  }, [filteredPrograms, checksheetMap, selectedChecksheets]);

  const handleSelectChecksheet = (checksheetId) => {
    if (!selectedChecksheets.includes(checksheetId))
      setSelectedChecksheets((prev) => [...prev, checksheetId]);
    else
      setSelectedChecksheets((prev) => prev.filter((p) => p !== checksheetId));
  };

  const handleSelectAllChecksheets = () => {
    const visibleChecksheetIds = filteredPrograms
      .map((program) => {
        const key = `${program.acadPlanCode}*${program.subplan?.acadSubPlanCode ?? 'null'}`;
        return checksheetMap.get(key);
      })
      .filter((checksheet) => !!checksheet)
      .map((checksheet) => checksheet.id);

    if (selectedChecksheets.length === visibleChecksheetIds.length) {
      setSelectedChecksheets([]);
      setAllChecksheetsSelected(false);
    } else {
      setSelectedChecksheets(visibleChecksheetIds);
      setAllChecksheetsSelected(true);
    }
  };

  if (fetchSubplanActivationList?.isError)
    return (
      <div>
        <div>
          An error has occurred: {fetchSubplanActivationList.error.message}
        </div>
        <div>
          Please refresh the page. If the error persists, please try again
          later.
        </div>
      </div>
    );

  return (
    <>
      <div className="pt-1 mt-2 position-relative">
        <div
          className={classNames(
            showDiv ? 'visible opacity-100' : 'invisible opacity-0',
            'container-xl position-fixed bg-white pt-4 program-list-header ps-0 pe-3'
          )}
          style={{ top: '89px', zIndex: 1 }}
        >
          <div className="mb-3">
            <Link to={'/'} className="text-maroon">
              Home
            </Link>
            <span className="text-gray-5 mx-1">/</span>
            {collegeCode ? collegeName : 'All colleges/schools'}
          </div>
          <div className="d-flex justify-content-between mb-2">
            {!!selectedChecksheets.length ? (
              <div className="d-flex gap-3 align-items-center">
                <div className="fw-bold">
                  {selectedChecksheets.length} selected
                </div>
                <div
                  className="button-link"
                  onClick={() => setSelectedChecksheets([])}
                  role="button"
                >
                  Deselect all
                </div>
                <Dropdown>
                  <Dropdown.Toggle
                    as={DropdownCustomToggle}
                    id="dropdown-custom-components"
                  >
                    <Button
                      label="Set status"
                      icon={['fa', 'chevron-down']}
                      color="maroon"
                    />
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="py-0" style={{ width: '248px' }}>
                    {StaticData.statusList.map((status) => (
                      <Dropdown.Item
                        key={status.value}
                        className="d-flex gap-1 px-3 py-2 text-wrap"
                        role="button"
                        onClick={() => handleSetProgress(status.value)}
                      >
                        <FontAwesomeIcon
                          icon={faCircle}
                          className={statusCircle[status.value].color}
                          width={'12px'}
                          style={{
                            marginTop: '4px',
                          }}
                        />
                        <div>{status.label}</div>
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
                <Button
                  label="Clear checksheets"
                  color="maroon"
                  onClick={handleClearChecksheets}
                />
              </div>
            ) : (
              <div />
            )}
            {fetchPrograms.isPending ? (
              <LoadingSkeleton width={'140px'} height={'25px'} />
            ) : (
              <span>
                {totalProgramsShown} results{' '}
                {!!searchString && (
                  <span>
                    for <span className="fw-bold">{searchString}</span>
                  </span>
                )}
              </span>
            )}
          </div>
          <div className="row border-bottom border-2 border-gray-7 py-1 mx-0">
            <div className="col-auto" style={{ width: '5%' }}>
              {userIsProvost && (
                <input
                  type="checkbox"
                  id="checkboxChecksheet-all"
                  data-ga-input="checkbox"
                  data-ga-input-name="onclick"
                  data-ga-input-event="select"
                  data-ga-input-action="click"
                  data-ga-input-region="main content"
                  data-ga-input-section="Select all checksheets"
                  checked={allChecksheetsSelected}
                  onChange={handleSelectAllChecksheets}
                  disabled={programChecksheetList?.length === 0}
                />
              )}
            </div>
            <div className="col-auto fw-bold ps-0" style={{ width: '23%' }}>
              Name
            </div>
            <div className="col-auto fw-bold" style={{ width: '12%' }}>
              Plan code
            </div>
            <div className="col-auto fw-bold" style={{ width: '15%' }}>
              Department
            </div>
            <div className="col-auto fw-bold" style={{ width: '17%' }}>
              Locations
            </div>
            <div className="col-auto fw-bold" style={{ width: '21%' }}>
              Status
              <FontAwesomeIcon
                icon={faCircleInfo}
                className="ms-1"
                onClick={handleOpenDefinitions}
                role="button"
              />
            </div>
            <div
              className="col-auto d-flex justify-content-end gap-1 align-items-center"
              style={{ width: '6%' }}
            />
          </div>
        </div>
        <div className="d-flex gap-6 justify-content-between mb-6">
          <div className="d-flex gap-3">
            <div>
              <div className="d-flex gap-1 align-items-center mb-1">
                <FontAwesomeIcon icon={faSearch} />
                <div className="fw-bold">Search</div>
              </div>
              <input
                type="text"
                placeholder={`Search ${
                  degreeType === 'undergrad'
                    ? 'undergraduate degrees'
                    : degreeType === 'minor'
                      ? 'minor programs'
                      : degreeType === 'cert'
                        ? 'certificate programs'
                        : 'programs'
                }`}
                className="border-0 border-bottom border-gray-6 bg-transparent px-3 py-1"
                style={{ width: '275px' }}
                onChange={(e) => handleSearch(e)}
              />
            </div>
            <div>
              <div className="d-flex gap-1 align-items-center mb-1">
                <FontAwesomeIcon icon={faFilter} />
                <div className="fw-bold">Filters</div>
              </div>
              <div className="filters d-flex flex-wrap gap-1 align-items-center">
                <Dropdown>
                  <Dropdown.Toggle
                    as={DropdownCustomToggle}
                    id="dropdown-custom-components"
                  >
                    <Button
                      label={locationLabel}
                      icon={['fa', 'chevron-down']}
                      classes={[
                        'btn',
                        locationFilters.length > 0 ? 'btn-maroon' : 'btn-white',
                        'btn-md',
                      ]}
                    />
                  </Dropdown.Toggle>

                  <Dropdown.Menu className="py-0" style={{ width: '340px' }}>
                    <Dropdown.Item
                      className="button-link px-3 mt-2"
                      role="button"
                      onClick={handleUncheckAllLocations}
                    >
                      Uncheck all
                    </Dropdown.Item>
                    {StaticData.campusUndergradList.map((campus) => (
                      <Dropdown.ItemText
                        key={campus.value}
                        className="d-flex gap-2 ps-3"
                        role="button"
                      >
                        <input
                          type="checkbox"
                          id={'checkbox-' + campus.value}
                          data-ga-input="checkbox"
                          data-ga-input-name="onclick"
                          data-ga-input-event="select"
                          data-ga-input-action="click"
                          data-ga-input-region="main content"
                          data-ga-input-section={campus.value}
                          value={campus.value}
                          checked={locationFilters.includes(campus.value)}
                          onChange={(e) => handleFilterLocation(e)}
                        />
                        <span>{campus.label}</span>
                      </Dropdown.ItemText>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
                <Dropdown>
                  <Dropdown.Toggle
                    as={DropdownCustomToggle}
                    id="dropdown-custom-components"
                  >
                    <Button
                      label={departmentLabel}
                      icon={['fa', 'chevron-down']}
                      classes={[
                        'btn',
                        departmentFilters.length > 0
                          ? 'btn-maroon'
                          : 'btn-white',
                        'btn-md',
                      ]}
                    />
                  </Dropdown.Toggle>

                  <Dropdown.Menu className="py-0" style={{ width: '540px' }}>
                    <Dropdown.Item
                      className="button-link px-3 mt-2"
                      role="button"
                      onClick={handleUncheckAllDepartments}
                    >
                      Uncheck all
                    </Dropdown.Item>
                    {departments
                      .toSorted(sortByDepartment)
                      .map(([key, value]) => (
                        <Dropdown.ItemText
                          key={key}
                          className="d-flex gap-2 ps-3"
                          role="button"
                        >
                          <input
                            type="checkbox"
                            id={'checkbox-' + key}
                            data-ga-input="checkbox"
                            data-ga-input-name="onclick"
                            data-ga-input-event="select"
                            data-ga-input-action="click"
                            data-ga-input-region="main content"
                            data-ga-input-section={key}
                            value={key}
                            checked={departmentFilters.includes(key)}
                            onChange={(e) => handleFilterDepartment(e)}
                          />
                          <span>{value}</span>
                        </Dropdown.ItemText>
                      ))}
                  </Dropdown.Menu>
                </Dropdown>
                <Dropdown>
                  <Dropdown.Toggle
                    as={DropdownCustomToggle}
                    id="dropdown-custom-components"
                  >
                    <Button
                      label={statusLabel}
                      icon={['fa', 'chevron-down']}
                      classes={[
                        'btn',
                        statusFilters.length > 0 ? 'btn-maroon' : 'btn-white',
                        'btn-md',
                      ]}
                    />
                  </Dropdown.Toggle>

                  <Dropdown.Menu className="py-0" style={{ width: '340px' }}>
                    <Dropdown.Item
                      className="button-link px-3 mt-2"
                      role="button"
                      onClick={handleUncheckAllStatuses}
                    >
                      Uncheck all
                    </Dropdown.Item>
                    <Dropdown.ItemText
                      className="d-flex gap-2 ps-3"
                      role="button"
                    >
                      <input
                        type="checkbox"
                        id="checkbox-NONE"
                        data-ga-input="checkbox"
                        data-ga-input-name="onclick"
                        data-ga-input-event="select"
                        data-ga-input-action="click"
                        data-ga-input-region="main content"
                        data-ga-input-section="NONE"
                        value="NONE"
                        checked={statusFilters.includes('NONE')}
                        onChange={(e) => handleFilterStatus(e)}
                      />
                      <span>No checksheet exists</span>
                    </Dropdown.ItemText>
                    {StaticData.statusList.map((status) => (
                      <Dropdown.ItemText
                        key={status.value}
                        className="d-flex gap-2 ps-3"
                        role="button"
                      >
                        <input
                          type="checkbox"
                          id={'checkbox-' + status.value}
                          data-ga-input="checkbox"
                          data-ga-input-name="onclick"
                          data-ga-input-event="select"
                          data-ga-input-action="click"
                          data-ga-input-region="main content"
                          data-ga-input-section={status.value}
                          value={status.value}
                          checked={statusFilters.includes(status.value)}
                          onChange={(e) => handleFilterStatus(e)}
                        />
                        <span>{status.label}</span>
                      </Dropdown.ItemText>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
                {(locationFilters.length > 0 ||
                  departmentFilters.length > 0 ||
                  statusFilters.length > 0) && (
                  <div
                    className="button-link"
                    onClick={handleClearAllFilters}
                    role="button"
                  >
                    Clear all filters
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="d-flex gap-2">
            <div>
              <div className="d-flex gap-1 align-items-center mb-1">
                <FontAwesomeIcon icon={faCalendar} />
                <div className="fw-bold text-nowrap">Catalog year</div>
              </div>
              <select value={year} onChange={(e) => handleSetYear(e)}>
                {StaticData.yearList.map((year) => (
                  <option key={year.value} value={year.value}>
                    {year.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <div className="d-flex gap-1 align-items-center mb-1">
                <FontAwesomeIcon icon={faSort} />
                <div className="fw-bold">Sort by</div>
              </div>
              <select onChange={(e) => handleSortBy(e)}>
                <option value="nameA-Z">Name (A-Z)</option>
                <option value="nameZ-A">Name (Z-A)</option>
              </select>
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-between mb-2">
          {!!selectedChecksheets.length ? (
            <div className="d-flex gap-3 align-items-center">
              <div className="fw-bold">
                {selectedChecksheets.length} selected
              </div>
              <div
                className="button-link"
                onClick={() => setSelectedChecksheets([])}
                role="button"
              >
                Deselect all
              </div>
              <Dropdown>
                <Dropdown.Toggle
                  as={DropdownCustomToggle}
                  id="dropdown-custom-components"
                >
                  <Button
                    label="Set status"
                    icon={['fa', 'chevron-down']}
                    color="maroon"
                  />
                </Dropdown.Toggle>
                <Dropdown.Menu className="py-0" style={{ width: '248px' }}>
                  {StaticData.statusList.map((status) => (
                    <Dropdown.Item
                      key={status.value}
                      className="d-flex gap-1 px-3 py-2 text-wrap"
                      role="button"
                      onClick={() => handleSetProgress(status.value)}
                    >
                      <FontAwesomeIcon
                        icon={faCircle}
                        className={statusCircle[status.value].color}
                        width={'12px'}
                        style={{
                          marginTop: '4px',
                        }}
                      />
                      <div>{status.label}</div>
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
              <Button
                label="Clear checksheets"
                color="maroon"
                onClick={handleClearChecksheets}
              />
            </div>
          ) : (
            <div />
          )}
          {fetchPrograms.isPending ? (
            <LoadingSkeleton width={'140px'} height={'25px'} />
          ) : (
            <span>
              {totalProgramsShown} results{' '}
              {!!searchString && (
                <span>
                  for <span className="fw-bold">{searchString}</span>
                </span>
              )}
            </span>
          )}
        </div>
        <div className="row border-bottom border-2 border-gray-7 py-1 mx-0">
          <div className="col-auto" style={{ width: '5%' }}>
            {userIsProvost && (
              <input
                type="checkbox"
                id="checkboxChecksheet-all"
                data-ga-input="checkbox"
                data-ga-input-name="onclick"
                data-ga-input-event="select"
                data-ga-input-action="click"
                data-ga-input-region="main content"
                data-ga-input-section="Select all checksheets"
                checked={allChecksheetsSelected}
                onChange={handleSelectAllChecksheets}
                disabled={programChecksheetList?.length === 0}
              />
            )}
          </div>
          <div className="col-auto fw-bold ps-0" style={{ width: '23%' }}>
            Name
          </div>
          <div className="col-auto fw-bold" style={{ width: '12%' }}>
            Plan code
          </div>
          <div className="col-auto fw-bold" style={{ width: '15%' }}>
            Department
          </div>
          <div className="col-auto fw-bold" style={{ width: '17%' }}>
            Locations
          </div>
          <div className="col-auto fw-bold" style={{ width: '21%' }}>
            Status
            <FontAwesomeIcon
              icon={faCircleInfo}
              className="ms-1"
              onClick={handleOpenDefinitions}
              role="button"
            />
          </div>
          <div
            className="col-auto d-flex justify-content-end gap-1 align-items-center"
            style={{ width: '6%' }}
          />
        </div>
        {!(fetchPrograms.isSuccess && programList.length > 0 && getChecksheetIsSuccess) ? (
          <div className="mt-1">
            {[...Array(8)].map((item, index) => (
              <LoadingSkeleton key={index} width={'100%'} height={'65px'} />
            ))}
          </div>
        ) : (
          totalProgramsShown > 0 ? (
            filteredPrograms.map((program, index) => (
              <ProgramRow
                key={
                  program.subplan
                    ? program.acadPlanCode +
                      '*' +
                      program.subplan.acadSubPlanCode
                    : program.acadPlanCode
                }
                program={program}
                activatedSubplans={
                  fetchSubplanActivationList?.data?.[program.acadPlanCode]
                }
                programList={programList}
                // idsList={idsList}
                checksheetMap={checksheetMap}
                filters={statusFilters}
                index={index}
                refetch={refetchChecksheets}
                isLoading={getChecksheetIsLoading}
                handleOpenDefinitions={handleOpenDefinitions}
                selectedChecksheets={selectedChecksheets}
                handleSelectChecksheet={handleSelectChecksheet}
                user={user}
              />
            ))
          ) : (
            <div className="p-2 fst-italic text-gray-6">
              No{' '}
              {degreeType === 'undergrad'
                ? 'undergraduate degrees'
                : degreeType === 'minor'
                  ? 'minor programs'
                  : degreeType === 'cert'
                    ? 'certificate programs'
                    : 'programs'}{' '}
              found
            </div>
          )
        )}
      </div>
      <Modal show={showModal} className="pt-8">
        <div className="d-flex gap-2 align-items-center p-3">
          <FontAwesomeIcon icon={faSpinner} spin />
          <div className="text-nowrap">Deleting checksheets...</div>
        </div>
      </Modal>
    </>
  );
};

export default ProgramRows;
